import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import { useMemo, type FC } from 'react';
import {
  PiUser,
  PiLightning,
  PiArchive,
  PiArrowsLeftRight,
  PiCaretRight,
  PiCaretDown,
  PiXLight,
  PiUserLight,
} from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { UserKind } from 'src/api/generated';
import type { FunnelStatus } from 'src/api/generated';
import { useUsersSelect } from 'src/modules/shared/hooks/users/select';
import { Text } from 'src/modules/shared/components/atoms/text';
import Dropdown from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { UNASSIGNED_USER_ID } from 'src/modules/communications/constants/filters.const';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface BulkActionsMenuProps {
  selectedCount: number;
  onUserAssign: (userId: number | null) => void;
  statuses?: FunnelStatus[];
  onMoveStatus?: (statusId: number) => void;
  onPowerDial?: () => void;
  onArchive?: () => void;
  isLoading?: boolean;
  powerDialingActive?: boolean;
}

/**
 * Compact menu dropdown for bulk actions with nested submenus.
 */
export const BulkActionsMenu: FC<BulkActionsMenuProps> = ({
  selectedCount,
  onUserAssign,
  statuses,
  onMoveStatus,
  onPowerDial,
  onArchive,
  isLoading = false,
  powerDialingActive,
}) => {
  const { t } = useI18n();
  const { users } = useAllPersonAndSystemUsers();
  const { userOptions } = useUsersSelect({
    users,
    options: {
      autoSelectCurrentUser: false,
      clearSelectionForAdmin: false,
    },
  });

  const { hasSystemUsers, personUserOptions, systemUserOptions } =
    useMemo(() => {
      const hasSystemUsers = userOptions.some(
        (opt) => opt.kind === UserKind.SYSTEM
      );
      const personUserOptions = userOptions.filter(
        (opt) => opt.kind === UserKind.PERSON
      );
      const systemUserOptions = userOptions.filter(
        (opt) => opt.kind === UserKind.SYSTEM
      );

      return { hasSystemUsers, personUserOptions, systemUserOptions };
    }, [userOptions]);

  const assignDropdownItems = useMemo((): DropdownItem[] => {
    const baseItems: DropdownItem[] = [
      {
        onClick: () => onUserAssign(UNASSIGNED_USER_ID),
        // TODO: check when implementing i18n
        content: 'Sin asignar',
        icon: (
          <PiXLight className="h-4 w-4 text-secondary-600 absolute right-4" />
        ),
      },
      ...personUserOptions.map((opt) => ({
        onClick: () => onUserAssign(Number(opt.value)),
        content: opt.label,
        icon: hasSystemUsers ? (
          <PiUserLight className="h-4 w-4 text-secondary-600" />
        ) : undefined,
        separator:
          hasSystemUsers &&
          systemUserOptions.length > 0 &&
          opt.value === personUserOptions[personUserOptions.length - 1]?.value,
      })),
    ];

    if (!hasSystemUsers) return baseItems;

    const systemUserItems = systemUserOptions.map((opt) => ({
      onClick: () => onUserAssign(Number(opt.value)),
      content: opt.label,
      icon: <AiRobotOutlineIcon className="h-4 w-4 text-secondary-600" />,
    }));

    return [...baseItems, ...systemUserItems];
  }, [hasSystemUsers, onUserAssign, personUserOptions, systemUserOptions]);

  const moveStatusDropdownItems = useMemo(() => {
    if (!onMoveStatus || !statuses || statuses.length === 0) return [];

    const items = statuses.map((status) => ({
      onClick: () => onMoveStatus(status.id),
      content: (
        <FunnelStatusName
          name={status.name}
          outcome={status.terminalOutcome}
          icon="archive"
          showArchiveTooltip
        />
      ),
    }));

    return items;
  }, [onMoveStatus, statuses]);

  if (selectedCount === 0) return null;

  const items: DropdownItem[] = [
    {
      onClick: () => undefined,
      disabled: isLoading,
      closeOnClick: false,
      content: (
        <Dropdown
          items={assignDropdownItems}
          handler={
            <div className="flex items-center justify-between w-full gap-2">
              <PiUser className="w-4 h-4 text-secondary-700" />
              <Text size="paragraph" weight="light" color="secondary">
                {/* TODO: check when implementing i18n */}
                Asignar
              </Text>
              <PiCaretRight className="w-4 h-4 text-secondary-700 ml-auto" />
            </div>
          }
          placement="right-start"
          size="sm"
          className="w-full"
          menuClassName="ml-3 -mt-2"
          mobileTitle={t(
            'communications.dropdown.bulkActions.assign.mobileTitle'
          )}
        />
      ),
    },
  ];

  if (onMoveStatus && statuses && statuses.length > 0) {
    items.push({
      onClick: () => undefined,
      disabled: isLoading,
      closeOnClick: false,
      content: (
        <Dropdown
          items={moveStatusDropdownItems}
          handler={
            <div className="flex items-center justify-between w-full gap-2">
              <PiArrowsLeftRight className="w-4 h-4 text-secondary-700" />
              <Text size="paragraph" weight="light" color="secondary">
                {/* TODO: check when implementing i18n */}
                Mover
              </Text>
              <PiCaretRight className="w-4 h-4 text-secondary-700 ml-auto" />
            </div>
          }
          placement="right-start"
          size="sm"
          className="w-full"
          menuClassName="ml-3 -mt-2"
          mobileTitle={t(
            'communications.dropdown.bulkActions.move.mobileTitle'
          )}
        />
      ),
    });
  }

  if (onArchive) {
    items.push({
      onClick: onArchive,
      disabled: isLoading,
      icon: <PiArchive className="w-4 h-4 text-secondary-700" />,
      // TODO: check when implementing i18n
      content: 'Archivar',
    });
  }

  if (onPowerDial) {
    items.push({
      onClick: onPowerDial,
      disabled: (powerDialingActive ?? false) || isLoading,
      content: (
        <Tooltip
          variant="secondary"
          size="sm"
          content={
            <div className="flex flex-col gap-2 p-2">
              {/* TODO: check when implementing i18n */}
              <Text size="tiny" weight="medium">
                Iniciará una sesión de discado rápido
              </Text>
              <Text size="tiny">
                • Llamará secuencialmente a cada número de cada contacto
              </Text>
              <Text size="tiny">• Terminará cuando se complete la cola</Text>
              <Text size="tiny">
                • Puedes pausar y reanudar en cualquier momento
              </Text>
            </div>
          }
        >
          <div className="flex items-center gap-2 w-full">
            <PiLightning className="w-4 h-4 text-secondary-700" />
            <Text size="paragraph" weight="light" color="secondary">
              Iniciar discado
            </Text>
          </div>
        </Tooltip>
      ),
    });
  }

  return (
    <MenuDropdown
      items={items}
      mobileTitle={t('communications.dropdown.bulkActions.mobileTitle')}
      handlerComponent={
        <Button
          variant="primary"
          appearance="soft"
          size="md"
          className="rounded-full px-3 py-1 flex items-center gap-1"
        >
          {selectedCount}
          {/* TODO: check when implementing i18n */}
          {selectedCount === 1 ? ' seleccionado' : ' seleccionados'}
          <PiCaretDown className="w-4 h-4" />
        </Button>
      }
      placement="bottom"
      size="sm"
      isLoading={isLoading}
      variant="primary"
    />
  );
};
